import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="segmented-input"
export default class extends Controller {
  static targets = ["segment", "hiddenInput", "submitButton", "container"]
  static values = { 
    length: { type: Number, default: 6 },
    autoSubmit: { type: Boolean, default: false },
    autoSubmitDelay: { type: Number, default: 100 },
    allowedPattern: { type: String, default: "\\d" }, // regex pattern
    autoAdvance: { type: Boolean, default: true },
    autoFocus: { type: Boolean, default: true }
  }

  connect() {
    if (this.autoFocusValue) {
      this.focusFirstInput()
    }
    this.updateSubmitButton()
  }

  handleInput(event) {
    const input = event.target
    const index = this.getInputIndex(input)
    
    // Filter input based on allowed pattern
    input.value = this.filterInput(input.value)
    
    // Auto-advance to next input if enabled and current is filled
    if (this.autoAdvanceValue && input.value && index < this.segmentTargets.length - 1) {
      this.segmentTargets[index + 1].focus()
    }
    
    this.updateValue()
  }

  handleKeydown(event) {
    const input = event.target
    const index = this.getInputIndex(input)
    
    switch(event.key) {
      case 'Backspace':
        // If current input is empty and not first input, focus previous
        if (!input.value && index > 0) {
          this.segmentTargets[index - 1].focus()
        }
        break
      case 'ArrowLeft':
        if (index > 0) {
          this.segmentTargets[index - 1].focus()
        }
        break
      case 'ArrowRight':
        if (index < this.segmentTargets.length - 1) {
          this.segmentTargets[index + 1].focus()
        }
        break
      case 'Delete':
        // Clear current input and all following inputs
        for (let i = index; i < this.segmentTargets.length; i++) {
          this.segmentTargets[i].value = ''
        }
        this.updateValue()
        break
      default:
        // Prevent input that doesn't match pattern
        if (!this.isValidInput(event.key) && !this.isControlKey(event.key)) {
          event.preventDefault()
        }
    }
  }

  handlePaste(event) {
    event.preventDefault()
    const pastedData = this.filterInput(event.clipboardData.getData('text'))
    
    if (pastedData.length <= this.lengthValue) {
      // Clear all inputs first
      this.clearAllInputs()
      
      // Fill inputs with pasted data
      pastedData.split('').forEach((char, i) => {
        if (this.segmentTargets[i]) {
          this.segmentTargets[i].value = char
        }
      })
      
      // Focus the next empty input or the last filled input
      const nextEmptyIndex = Math.min(pastedData.length, this.segmentTargets.length - 1)
      this.segmentTargets[nextEmptyIndex].focus()
      
      this.updateValue()
    }
  }

  // Public API methods
  clear() {
    this.clearAllInputs()
    this.updateValue()
    if (this.autoFocusValue) {
      this.focusFirstInput()
    }
  }

  setValue(value) {
    const filteredValue = this.filterInput(value.toString())
    this.clearAllInputs()
    
    filteredValue.split('').forEach((char, i) => {
      if (this.segmentTargets[i]) {
        this.segmentTargets[i].value = char
      }
    })
    
    this.updateValue()
  }

  getValue() {
    return this.segmentTargets.map(input => input.value).join('')
  }

  isComplete() {
    return this.getValue().length === this.lengthValue
  }

  focus() {
    this.focusFirstInput()
  }

  // Private methods
  updateValue() {
    const value = this.getValue()
    
    // Update hidden input
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = value
    }
    
    // Update submit button state
    this.updateSubmitButton()
    
    // Dispatch custom event
    this.dispatch("change", { 
      detail: { 
        value: value, 
        isComplete: this.isComplete(),
        length: value.length 
      } 
    })
    
    // Auto-submit if enabled and input is complete
    if (this.autoSubmitValue && this.isComplete()) {
      setTimeout(() => {
        this.submitForm()
      }, this.autoSubmitDelayValue)
    }
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !this.isComplete()
    }
  }

  submitForm() {
    const form = this.element.closest('form')
    if (form) {
      this.dispatch("submit", { detail: { value: this.getValue() } })
      form.requestSubmit()
    }
  }

  clearAllInputs() {
    this.segmentTargets.forEach(input => input.value = '')
  }

  focusFirstInput() {
    if (this.segmentTargets.length > 0) {
      this.segmentTargets[0].focus()
    }
  }

  getInputIndex(input) {
    return this.segmentTargets.indexOf(input)
  }

  filterInput(input) {
    const regex = new RegExp(this.allowedPatternValue, 'g')
    return input.match(regex)?.join('') || ''
  }

  isValidInput(key) {
    const regex = new RegExp(`^${this.allowedPatternValue}$`)
    return regex.test(key)
  }

  isControlKey(key) {
    const controlKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'
    ]
    return controlKeys.includes(key) || key.startsWith('F') || 
           (key.length === 1 && (event.ctrlKey || event.metaKey))
  }

  // Value change callbacks for configuration updates
  lengthValueChanged() {
    // Could dynamically adjust number of inputs if needed
    this.updateSubmitButton()
  }

  autoSubmitValueChanged() {
    // Recheck if we should auto-submit current state
    if (this.autoSubmitValue && this.isComplete()) {
      setTimeout(() => this.submitForm(), this.autoSubmitDelayValue)
    }
  }
}
